import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { CloudUpload, CloudDownload, FileText, Key } from 'lucide-react';
import { StatusCard } from './ksef/StatusCard';
import { QueryTab } from './ksef/QueryTab';
import { RecordsTab } from './ksef/RecordsTab';
import { UploadTab } from './ksef/UploadTab';
import { TokensTab } from './ksef/TokensTab';

const API = process.env.REACT_APP_BACKEND_URL;

const KSeFTab = ({ user }) => {
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin';

  const [status, setStatus] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [users, setUsers] = useState([]);
  const [authenticating, setAuthenticating] = useState(false);

  const fetchStatus = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/ksef/status`); setStatus(r.data); } catch {}
  }, []);

  const fetchTokens = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/ksef/tokens`); setTokens(r.data.tokens || []); } catch {}
  }, []);

  const fetchUsers = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/users`); setUsers(r.data || []); } catch {}
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchTokens();
    if (isAdmin) fetchUsers();
  }, []);

  const handleAuthenticate = async (tokenId) => {
    setAuthenticating(true);
    try {
      const r = tokenId
        ? await axios.post(`${API}/api/ksef/tokens/${tokenId}/authenticate`)
        : await axios.post(`${API}/api/ksef/authenticate`);
      if (r.data.success) {
        toast.success(`Authenticated with KSeF${r.data.token_name ? ` (${r.data.token_name})` : ''}`);
        await fetchTokens();
        await fetchStatus();
      } else {
        toast.error(r.data.error || 'Authentication failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Authentication failed');
    } finally {
      setAuthenticating(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Find which token is currently active and log THAT one out
      // (without a token_id, the backend logs out the env-singleton, leaving
      // user-managed tokens stuck in "session_active=true" state).
      const activeToken = tokens.find(t => t.session_active);
      const body = activeToken ? { token_id: activeToken.id } : {};
      await axios.post(`${API}/api/ksef/logout`, body);
      toast.success(`KSeF session terminated${activeToken ? ` (${activeToken.name})` : ''}`);
      fetchStatus(); fetchTokens();
    } catch { toast.error(t('invoiceExtra.failedToTerminateSession')); }
  };

  const sessionActive = status?.session_active || tokens.some(t => t.session_active);
  const sessionEnv = status?.environment || 'prod';

  return (
    <div className="space-y-4">
      <StatusCard
        status={status}
        tokens={tokens}
        sessionActive={sessionActive}
        sessionEnv={sessionEnv}
        authenticating={authenticating}
        onAuthenticate={handleAuthenticate}
        onLogout={handleLogout}
        onRefresh={() => { fetchStatus(); fetchTokens(); }}
      />

      <Tabs defaultValue="download">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="upload" data-testid="tab-upload">
            <CloudUpload className="h-3.5 w-3.5 mr-1" />{t('invoiceExtra.uploadInvoices')}
          </TabsTrigger>
          <TabsTrigger value="download" data-testid="tab-download">
            <CloudDownload className="h-3.5 w-3.5 mr-1" />{t('invoiceExtra.downloadInvoices')}
          </TabsTrigger>
          <TabsTrigger value="records" data-testid="tab-records">
            <FileText className="h-3.5 w-3.5 mr-1" />{t('invoiceExtra.records')}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="tokens" data-testid="tab-tokens">
              <Key className="h-3.5 w-3.5 mr-1" />{t('invoiceExtra.tokens')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="upload">
          <UploadTab />
        </TabsContent>

        <TabsContent value="download">
          <QueryTab tokens={tokens} sessionEnv={sessionEnv} />
        </TabsContent>

        <TabsContent value="records">
          <RecordsTab isAdmin={isAdmin} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="tokens">
            <TokensTab
              tokens={tokens}
              users={users}
              onTokensChanged={fetchTokens}
              onAuthenticate={handleAuthenticate}
              authenticating={authenticating}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default KSeFTab;
